import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchProductDetail } from "@/lib/queries";
import { imageSrc } from "@/lib/imageUrl";
import type { ProductDetailData } from "@/types/sanity";
import { useSeo } from "@/seo/useSeo";
import { notFoundSeo, productDetailSeo } from "@/seo/detailSeo";

import Table from "@/components/elements/Table/Table";
import TitleBar from "@/components/elements/TitleBar/TitleBar";
import ImageWithContentLeft from "@/components/elements/ImageWithContent/ImageWithContentLeft";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import CustomLists from "@/components/elements/CustomLists/CustomLists";
import { PortableText, type PortableTextComponents } from "@portabletext/react";


const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const portableComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => (
      <p
        className="table-text-size text-justify tracking-tight break-normal word-spacing-wide leading-relaxed"
        style={{ all: 'unset', display: 'block' }} // reset inherited styles, keep block display
      >
        {children}
      </p>
    ),
  },
};

// The hero image renders at most ~600px wide; request 2x for high-DPI screens.
const HERO_IMAGE_WIDTH = 1200;

const splitIntoColumns = (arr: string[], columns = 2): string[][] => {
  const result: string[][] = Array.from({ length: columns }, () => []);
  arr.forEach((item, index) => {
    result[index % columns].push(item);
  });
  return result;
};

const SanityProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<ProductDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    const controller = new AbortController();

    setLoading(true);
    fetchProductDetail(slug, { signal: controller.signal })
      .then(setProduct)
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("Sanity fetch error:", err);
        setProduct(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [slug]);

  useSeo(
    product
      ? productDetailSeo({
          name: product.title,
          description: product.description,
          image: product.image ? imageSrc(product.image, HERO_IMAGE_WIDTH) : undefined,
          path: `/products/${slug}`,
        })
      : loading
        ? null
        : notFoundSeo("Product")
  );

  if (loading) return <p className="mt-[5rem] min-h-screen p-10 text-center">Loading...</p>;
  if (!product) return <p className="mt-[5rem] min-h-screen p-10 text-center">Product not found</p>;

  return (
    <div className="pb-0 px-4 2xl:px-40 xl:px-40 md:px-[3rem] my-[5rem]">
      <TitleBar title={product.title} />

      {product.image && (
        <motion.div {...fadeInUp}>
          <ImageWithContentLeft
            imageUrl={imageSrc(product.image, HERO_IMAGE_WIDTH)}
            alt={product.title}
            title="Description"
            content={product.description}
          />
        </motion.div>
      )}

    {product.advantages?.length > 0 && (
  <motion.div {...fadeInUp}>
    <TitleWithContent title={`Advantages`}>
      {product.advantagesNote && (
        <PortableText value={product.advantagesNote} components={portableComponents} />
      )}
      <CustomLists layout="split" items={splitIntoColumns(product.advantages, 2)} />
    </TitleWithContent>
  </motion.div>
)}

{product.uses?.length > 0 && (
  <motion.div {...fadeInUp}>
    <TitleWithContent title="Uses">
      {product.usesNote && (
        <PortableText value={product.usesNote} components={portableComponents} />
      )}
      <CustomLists layout="split" items={splitIntoColumns(product.uses, 2)} />
    </TitleWithContent>
  </motion.div>
)}

      {product.propertyTable?.length > 0 && (
      <motion.div {...fadeInUp}>
        <TitleWithContent title="Properties">
          <div className="table-wrapper max-w-5xl mx-auto overflow-x-auto ">
          <Table
            items={{
              rows: product.propertyTable.map((row) => [row.property, row.value])
            }}
          />
          </div>
        </TitleWithContent>
      </motion.div>
     )}

      {product.packing && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title="Packing">
            <PortableText value={product.packing} components={portableComponents}/>
          </TitleWithContent>
        </motion.div>
      )}

    </div>
  );
};

export default SanityProductDetailPage;
